// GUI:
import { Panels } from '@gui/constants/Panels';
import { Box, Button, IconButton, Menu, MenuItem, Tab, Tabs, Typography } from '@mui/material';
import { AlignVerticalCenter01, ChevronSelectorVertical } from '@untitledui-pro/icons/line';
import React, { CSSProperties, useMemo, useRef, useState } from 'react';
import usePersistentStore, { useDesignSchemesBucket, useGlobalBucket } from '../../store/persistent/persistentStore';

// 3D Geometry:
import { ControllerManager } from '@gui/controller/ControllerManager';
import { DrawModule } from '@rendering/DrawingModule/DrawingModule';
import { UMBetaBadge } from '@urbanmetrix/ui-kit';
import { ContextState } from '@visualizer/types';
import DesignSchemeSetView from '../DesignSchemes/DesignSchemeSetView';
import { useTheme } from '@mui/material/styles';

type DesignSchemesProps = {
    isCollapsed?: boolean;
    onToggleCollapse?: () => void;
    panelsExpanded: Record<string, boolean>;
    style?: CSSProperties;
};

// =====================================
// HOOKS
// =====================================

function useDesignSchemeSets() {
    const setsState = useDesignSchemesBucket((s) => s.sets);
    const controller = ControllerManager.getInstance().getDesignSchemeController();

    const designSchemeSets = useMemo(() => controller.getDesignSchemeSets(), [setsState]);

    // Recomputes them ONLY when designSchemes.sets change in Zustand, not at every re-render:
    return designSchemeSets;
}

function useSelectedDesignSchemeSet() {
    const designSchemeSetsState = useDesignSchemesBucket((s) => s.sets);
    const selectedSetId = useDesignSchemesBucket((s) => s.selectedSetId);
    const controller = ControllerManager.getInstance().getDesignSchemeController();

    const selectedSet = useMemo(
        () => (selectedSetId ? controller.getDesignSchemeSet(selectedSetId) : undefined),
        [selectedSetId, designSchemeSetsState]
    );

    // Recomputes them ONLY when designSchemes.sets change in Zustand, not at every re-render:
    return selectedSet;
}

const DesignSchemes: React.FC<DesignSchemesProps> = ({ isCollapsed, onToggleCollapse, panelsExpanded, style }) => {
    // Controllers:
    const designSchemesController = ControllerManager.getInstance().getDesignSchemeController();
    const pipelineController = ControllerManager.getInstance().getPipelineController();

    // Zustand Actions:
    const setPanel = usePersistentStore((s) => s.setPanel);
    const setContextPanel = (updates: Partial<ContextState>) => setPanel(updates, Panels.CONTEXT);

    // DesignScheme Sets:
    const designSchemeSets = useDesignSchemeSets();
    const designSchemeSetsState = usePersistentStore((s) => s.designSchemes.sets);
    const selectedDesignSchemeSet = useSelectedDesignSchemeSet();
    const isVisible = selectedDesignSchemeSet?.isVisible() ?? true;

    // React State:
    const theme = useTheme();
    const polygonDrawerRefs = useRef<Record<string, DrawModule>>({});
    const context = useGlobalBucket((s) => s.context);
    const [activeTab, setActiveTab] = useState<'designSchemes' | 'compare'>('designSchemes');

    // Variables:
    const expandedCount = Object.values(panelsExpanded).filter(Boolean).length;
    const disableToggle = !isCollapsed && expandedCount === 1;

    // Handlers:
    const disposeDrawModule = (id: string) => {
        const module = polygonDrawerRefs.current[id];
        if (module) {
            module.stop(); // detach events & controls
            module.visualEffects.removeSpheres(); // remove sphere meshes
            module.visualEffects.removePolyLine(); // remove line geometry
            module.dispose();
            delete polygonDrawerRefs.current[id]; // clear reference
        }
    };

    const handleCollapseToggle = () => {
        if (onToggleCollapse) onToggleCollapse();
    };

    // Add / Visible / Open button:
    const [openAnchorEl, setOpenAnchorEl] = useState<null | HTMLElement>(null);
    const openMenuOpen = Boolean(openAnchorEl);

    const handleOpenMenu = (e: React.MouseEvent<HTMLButtonElement>) => {
        if (hasSets) setOpenAnchorEl(e.currentTarget);
    };

    const handleOpenMenuClose = () => setOpenAnchorEl(null);

    const hasSets = designSchemeSets.length > 0;

    // DesignScheme Creation:
    const [isCreating, setIsCreating] = useState(false);

    return (
        <div
            className={`data-panel data-panel--design-schemes ${isCollapsed ? 'data-panel--collapsed' : ''}`}
            style={style}
            data-guided-tour="guided-tour-design-schemes-panel"
        >
            <div className="data-panel-tabs">
                <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
                    <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                        <Tabs
                            value={activeTab}
                            onChange={(_, newValue) => setActiveTab(newValue as 'designSchemes' | 'compare')}
                            aria-label="Design Schemes panel tab"
                            sx={{
                                '& .MuiTabs-flexContainer': {
                                    gap: 2,
                                    mr: 2.5,
                                },
                            }}
                        >
                            <Tab
                                value="designSchemes"
                                label={
                                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, lineHeight: 0 }}>
                                        <Typography component="span" sx={{ lineHeight: 0 }}>
                                            DESIGN SCHEMES
                                        </Typography>
                                        <UMBetaBadge theme={theme.palette.mode === 'dark' ? 'dark' : 'light'} />
                                    </Box>
                                }
                                disabled={isCollapsed}
                            />
                            <Tab value="compare" label="COMPARE" disabled={isCollapsed} />
                        </Tabs>
                    </Box>
                    <IconButton
                        size="small"
                        onClick={handleCollapseToggle}
                        disabled={disableToggle}
                        sx={{
                            position: 'absolute',
                            right: -8,
                            boxShadow: 'none',
                            backgroundColor: 'transparent',
                            '&:hover': {
                                boxShadow: 'none',
                                backgroundColor: 'transparent',
                            },
                            '&:active': {
                                boxShadow: 'none',
                            },
                            '&:focus-visible': {
                                boxShadow: 'none',
                            },
                        }}
                    >
                        <Box
                            component="span"
                            sx={{
                                display: 'inline-flex',
                                transition: 'transform 120ms ease',
                                transform: isCollapsed ? 'rotate(0deg)' : 'rotate(180deg)',
                            }}
                        >
                            {isCollapsed ? (
                                <ChevronSelectorVertical className="icon-size-small" />
                            ) : (
                                <AlignVerticalCenter01 className="icon-size-small" />
                            )}
                        </Box>
                    </IconButton>
                </Box>
            </div>

            {!isCollapsed && (
                <div className="data-panel-content">
                    {activeTab === 'designSchemes' ? (
                        <div className="data-panel-scroll">
                            {/* === HEADER === */}
                            <Box display="flex" alignItems="center" gap={1} flexWrap="nowrap" sx={{ my: 0.5 }}>
                                {/* --- New --- */}
                                <Button
                                    data-testid="new-ds-set"
                                    size="small"
                                    variant="contained"
                                    sx={{ flex: 1, minWidth: 0 }}
                                    onClick={() => {
                                        setIsCreating(true);
                                    }}
                                >
                                    New
                                </Button>

                                {/* --- Open --- */}
                                <Button
                                    size="small"
                                    variant="contained"
                                    disabled={!hasSets}
                                    sx={{ flex: 1, minWidth: 0 }}
                                    onClick={handleOpenMenu}
                                >
                                    Open
                                </Button>

                                <Menu anchorEl={openAnchorEl} open={openMenuOpen} onClose={handleOpenMenuClose}>
                                    {designSchemeSets.map((proj) => {
                                        const label = designSchemeSetsState[proj.getID()]?.name ?? proj.getName(); // sameType.length === 1 ? typeLabel : `${typeLabel} ${number}`;

                                        return (
                                            <MenuItem
                                                key={proj.getID()}
                                                onClick={() => {
                                                    designSchemesController.updateSelectedDesignSchemeSet(proj.getID());
                                                    pipelineController.run();
                                                    handleOpenMenuClose();
                                                }}
                                            >
                                                {label}
                                            </MenuItem>
                                        );
                                    })}
                                </Menu>

                                {/* --- Visible --- */}
                                <Button
                                    size="small"
                                    variant={isVisible ? 'contained' : 'outlined'}
                                    disabled={!selectedDesignSchemeSet}
                                    sx={{
                                        flex: 1,
                                        minWidth: 0,
                                        ...(isVisible
                                            ? {}
                                            : {
                                                  borderWidth: 1,
                                              }),
                                    }}
                                    onClick={() => {
                                        if (!selectedDesignSchemeSet) return;

                                        const setId = selectedDesignSchemeSet.getID();
                                        const nextVisible = !isVisible;
                                        designSchemesController.updateDesignSchemeSetVisibility(setId, nextVisible);
                                        pipelineController.run();
                                    }}
                                >
                                    Visible
                                </Button>

                                {/* --- Delete --- */}
                                <Button
                                    data-testid="delete-ds-set"
                                    size="small"
                                    variant="contained"
                                    color="error"
                                    disabled={!selectedDesignSchemeSet}
                                    sx={{ flex: 1, minWidth: 0 }}
                                    onClick={() => {
                                        if (!selectedDesignSchemeSet) return;
                                        const setId = selectedDesignSchemeSet.getID();

                                        // 1) Dispose ALL DrawModules first:
                                        designSchemesController.getFootprints(setId)?.forEach((fp) => {
                                            disposeDrawModule(fp.id);
                                        });

                                        // 2) Remove Set & Footprints:
                                        designSchemesController.removeDesignSchemeSet(setId);

                                        // 3) Update to the another set (if any):
                                        const allSets = designSchemesController.getDesignSchemeSets();
                                        designSchemesController.updateSelectedDesignSchemeSet(allSets[0]?.getID());
                                        pipelineController.run();
                                    }}
                                >
                                    Delete
                                </Button>
                            </Box>

                            {/* DESIGNSCHEME SET UI */}
                            <DesignSchemeSetView
                                selectedProject={selectedDesignSchemeSet}
                                isCreating={isCreating}
                                onCancelCreate={() => setIsCreating(false)}
                                polygonDrawerRefs={polygonDrawerRefs.current}
                                setContextPanel={setContextPanel}
                                context={context}
                            />
                        </div>
                    ) : (
                        <Box
                            sx={{
                                flex: 1,
                                display: 'flex',
                                alignItems: 'flex-start',
                                justifyContent: 'center',
                                pt: 4,
                            }}
                        >
                            <Typography variant="h6">Coming soon!</Typography>
                        </Box>
                    )}
                </div>
            )}
        </div>
    );
};

export default DesignSchemes;
